import RightContent from '@/components/RightContent'
import { setLocale } from '@@/plugin-locale/localeExports'
import type { MenuDataItem, Settings as LayoutSettings } from '@ant-design/pro-layout'
import { PageLoading } from '@ant-design/pro-layout'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { LabeledValue } from 'antd/lib/select'
import { parse } from 'qs'
import React, { createElement, type FC, type ReactNode } from 'react'
import type { RequestConfig, RunTimeLayoutConfig } from 'umi'
import { getLocale, history } from 'umi'
import type { RequestInterceptor, ResponseError, ResponseInterceptor } from 'umi-request'
import packageJson from '../package.json'
import IconFont from './components/IconFont'
import { i18nMap } from './global'
import { AntdProvider } from './providers/AntdProvider'
import { NodeProvider } from './providers/NodeProvider'
import { SlotProvider } from './providers/SlotProvider'
import { currentUser as queryCurrentUser, getMenuTreeBase } from './services/ant-design-pro/api'
import { getSetting } from './services/basic'

// 获取用户信息比较慢的时候会展示一个 Loading
export const initialStateConfig = {
  loading: <PageLoading />,
}

export interface SearchMenu extends LabeledValue {
  route: string
  pageData?: string | null
  subPages?: SearchMenu[]
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(ignorePath?: boolean): Promise<{
  settings?: Partial<LayoutSettings>
  currentUser?: API.CurrentUser
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>
  currentTenant?: API.CurrentTenant
  fetchTenantInfo?: () => Promise<API.CurrentTenant | undefined>
  collapsed?: boolean
  menus?: MenuDataItem[]
  flatMenus?: MenuDataItem[]
  currencySymbol?: string
}> {
  const fetchUserInfo = async () => {
    try {
      const { data: currentUser } = await queryCurrentUser()

      const elements = currentUser.elements
      const handlerElements: Record<string, any> = {}
      for (const attr in elements) {
        // TODO: 历史原因, 需要加上去掉full_后的权限code 后续统一
        const handlerAttr = attr.replace(/full_/g, '') as string
        handlerElements[handlerAttr] = elements[attr]
      }
      const useInfo = {
        ...currentUser,
        elements: {
          ...elements,
          ...handlerElements,
        },
      }
      console.log('useInfo', useInfo)
      return useInfo
    } catch (error) {
      console.log(error)
    }
    return undefined
  }
  const fetchTenantInfo = async () => {
    try {
      // const { data: currentTenant } = await queryCurrentTenant({ domain });
      // return currentTenant  ;
      return { id: 1 }
    } catch (error) {
      console.log(error)
    }
    return undefined
  }
  const currentTenant = await fetchTenantInfo()
  let currencySymbol
  try {
    const settings = await getSetting({ confKey: 'tenant:currency-symbol' })
    currencySymbol = settings?.data?.confValue || '￥'
  } catch (error) {
    console.log(error)
  }

  const token = localStorage.getItem('Access-Token')

  // 如果是登录页面，不执行
  if (ignorePath === true || (history.location.pathname.toLowerCase() !== '/user/login' && token)) {
    const currentUser = await fetchUserInfo()
    const menuTreeBase = await getMenuTreeBase()

    function excludeEmptyArray<T extends unknown[]>(array: T) {
      return array.length > 0 ? array : undefined
    }

    // TODO: 当前 path 并不规范, 这里统一未来的格式, 去掉 `/root` 前缀
    function excludeRootPrefix(path: string) {
      return path.startsWith('/root') ? path.slice(5) : path
    }

    function convertMenuTreeBase(menuTreeBase: Record<string, unknown>[]) {
      function _convertMenuTreeBase(menuTreeBase: Record<string, unknown>[], parent?: MenuDataItem): MenuDataItem[] {
        return menuTreeBase
          .filter((m) => currentUser?.elements?.[m.authCode as string] === true)
          .filter((m) => m.type === 1)
          .map((m) => {
            const menuWithParent: MenuDataItem = {
              icon: m.icon ? <IconFont type={m.icon as string} /> : undefined,
              locale: false,
              name: m.name as string,
              path: excludeRootPrefix(m.path as string),
              redirect: m.redirect ? excludeRootPrefix(m.redirect as string) : undefined,
              hideInMenu: m.extData === '0',
              parent,
            }
            if (m.children) {
              menuWithParent.redirect = composeAbsolutePath(menuWithParent, 'redirect')
            }
            return {
              ...menuWithParent,
              path: composeAbsolutePath(menuWithParent, 'path'),
              children: m.subPermission
                ? excludeEmptyArray(_convertMenuTreeBase(m.subPermission as typeof menuTreeBase, menuWithParent))
                : undefined,
            }
          })
      }
      return _convertMenuTreeBase(menuTreeBase)
    }

    function composeAbsolutePath(menu: MenuDataItem & { parent?: Record<string, unknown> }, field: string): string {
      if (!menu[field]) {
        return ''
      }
      if (menu.parent) {
        if (menu[field].startsWith('/')) {
          return menu[field]
        }
        return composeAbsolutePath(menu.parent, field) + '/' + menu[field]
      }
      return menu[field]
    }

    function flatSubsystemMenuTree(menuTree: MenuDataItem[]): MenuDataItem[] {
      return menuTree.reduce<MenuDataItem[]>(
        (acc, m) =>
          acc.concat(
            m === null
              ? []
              : 'children' in m && Array.isArray(m.children) && m.children.length > 0
                ? [m, ...flatSubsystemMenuTree(m.children)]
                : m,
          ),
        [],
      )
    }

    const menus = convertMenuTreeBase(menuTreeBase)
    const flatMenus = flatSubsystemMenuTree(menus)

    return {
      fetchUserInfo,
      currentUser,
      currentTenant,
      fetchTenantInfo,
      settings: {},
      collapsed: false,
      menus,
      flatMenus,
      currencySymbol,
    }
  }
  return {
    fetchUserInfo,
    currentTenant,
    fetchTenantInfo,
    settings: {},
    collapsed: false,
    menus: [],
    flatMenus: [],
    currencySymbol,
  }
}

// 保存节点与所在位置的映射关系
const nodeMap = new Map<Node, (rootNode: Node | undefined) => void>()

export const qiankun: {
  bootstrap: (props: Record<string, any> | undefined) => Promise<void>
  mount: (props: Record<string, any> | undefined) => Promise<void>
  unmount: (props: Record<string, any> | undefined) => Promise<void>
} = {
  async bootstrap() {},
  async mount(props) {
    props?.updateConfig?.({
      version: packageJson.version,
    })
    window.qiankunProps = props
  },
  async unmount() {
    // 删除主系统外部节点, 防止全局污染. 子系统内部节点随着组件卸载自动销毁
    for (const [node] of nodeMap) {
      node.parentElement?.removeChild(node)
    }
  },
}

const queryClient = new QueryClient()

export function rootContainer(container: ReactNode) {
  return (
    <QueryClientProvider client={queryClient}>
      <NodeProvider nodeMap={nodeMap}>
        <AntdProvider>
          <SlotProvider>{container}</SlotProvider>
        </AntdProvider>
      </NodeProvider>
    </QueryClientProvider>
  )
}

// https://umijs.org/zh-CN/plugins/plugin-layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const layoutConfig: ReturnType<RunTimeLayoutConfig> = {
    collapsed: initialState?.collapsed,
    onCollapse: (collapsed: boolean) => {
      setInitialState({ ...initialState, collapsed })
    },
    title: false,
    logo: () => <img src={initialState?.currentTenant?.logo || '/lanxi_logo.jpg'} style={{ width: '100%', height: 'auto' }} />,
    className: initialState?.collapsed ? 'collapsed' : 'not-collapsed',
    menuHeaderRender: () => <img src={initialState?.currentTenant?.logo || '/icons/icon-128x128.png'} style={{ height: 'auto' }} />,
    menu: {
      params: {
        lang: getLocale(),
        userId: initialState?.currentUser?.id,
      },
      request: () => Promise.resolve(initialState?.menus ?? []),
    },
    menuDataRender: (menuData: MenuDataItem[]) => {
      return [...menuData]
    },
    breadcrumbRender: false,
    rightContentRender: () => createElement(RightContent as FC),
    headerContentRender: (_props, defaultDom) => defaultDom,
    disableContentMargin: false,
    onPageChange() {
      // ⚠️ 在切换主系统的路由时, 也会触发这里的函数, 可能会被此处拦截, 无法跳转到主系统对应的页面
      // 这里通过判断url是否没有子系统前缀来确定是否已经unmount了
      // 不要使用history.location.pathname判断, 会把子系统前缀忽略
      if (!window.location.pathname.startsWith('/health-management')) {
        return
      }

      // 切换页面时，清空 window.localeList
      window.localeList = []

      const exclude = ['/', '/user/login', '/noAccess']
      const hasPermission = initialState?.flatMenus?.some((m) => m.path === history.location.pathname)
      if (!exclude.includes(history.location.pathname) && !hasPermission) {
        console.debug('没有权限访问', history.location.pathname, initialState)
        history.replace('/noAccess')
        return
      }

      // 进入客户详情则关闭侧边栏
      setInitialState({
        ...initialState,
        collapsed: history.location.pathname === '/Customer/CustomerDetail',
      })
    },
    // menuHeaderRender: undefined,
    ...initialState?.settings,
  }

  if (window.__POWERED_BY_QIANKUN__) {
    layoutConfig.menuRender = false
    layoutConfig.headerRender = false
  }

  return layoutConfig
}

export interface SucceedResponseVO<T> {
  code: string
  data: T
  message: string
  success: true
}

export interface FailedResponseVO {
  code: string
  message: string
  success: false
}

export type ResponseVO<T> = SucceedResponseVO<T> | FailedResponseVO

/**
 * 异常处理程序
 * @see https://beta-pro.ant.design/docs/request-cn
 */
const errorHandler = (error: ResponseError<ResponseVO<unknown>>) => {
  // 如果是乾坤子系统就调用主系统处理错误
  if (window.__POWERED_BY_QIANKUN__) {
    window.qiankunProps?.responseErrorHandler?.(error.response)
    return
  }

  // ⚠️ 子系统调用主系统的错误处理函数后, 可能在主系统重定向到登录页面, 导致子系统unmount, 但是子系统的异常处理有可能在unmount后还会执行, 导致 window.__POWERED_BY_QIANKUN__ 变为 false
  // 这里通过判断url是否没有子系统前缀来确定是否已经unmount了
  // 不要使用history.location.pathname判断, 会把子系统前缀忽略
  if (!window.location.pathname.startsWith('/health-management')) {
    return
  }

  const needToken = error.data.code === '0000001' || error.data.code === '1000011' || error.data.code === '1000013'

  if (needToken && history.location.pathname.toLowerCase() !== '/user/login') {
    history.replace({
      pathname: '/user/login',
      query: {
        redirect: history.location.pathname + history.location.search,
      },
    })
  }
}

const requestInterceptor: RequestInterceptor = (url, options) => {
  const { newToken = '', language = '' } = history.location.search ? parse(history.location.search) : {}
  if (newToken) {
    localStorage.setItem('Access-Token', newToken as string)
  }
  if (language) {
    setLocale(language as string)
  }
  let localToken = localStorage.getItem('Access-Token')
  const { headers } = options

  if (localToken) {
    if (!new RegExp(/^[b|B]earer/).test(localToken)) {
      localToken = `Bearer ${localToken}`
    }
    options.headers = {
      ...headers,
      Authorization: localToken ?? '',
    }
  }
  // 国际化
  const locale = i18nMap?.[getLocale() as keyof typeof i18nMap] || 'zh_CN'
  options.params = { ...options.params, lang: locale }
  return {
    url,
    options,
  }
}

const responseInterceptor: ResponseInterceptor = (response, options) => {
  if ((options as any).headers.authorization) {
    const token = localStorage.getItem('Access-Token')
    let newToken = (options as any).headers.authorization
    newToken = newToken.replace('Bearer ', '')
    if (token && token !== newToken) {
      localStorage.setItem('Access-Token', newToken)
    }
  }
  // const token = localStorage.getItem('Access-Token')
  // const { headers } = options
  // if (token) {
  //   (headers as {})['Authorization'] = `Bearer ${token}` // 让每个请求携带自定义 token 请根据实际情况自行修改
  // }
  // eslint-disable-next-line
  // options.headers = headers
  return response
}

// https://umijs.org/zh-CN/plugins/plugin-request
export const request: RequestConfig = {
  errorHandler,
  prefix: '/api',
  requestInterceptors: [requestInterceptor],
  responseInterceptors: [responseInterceptor],
}
